import React, { useState, useEffect, useRef } from 'react';
import { ocrImage } from './api';
import CameraComponent from './CameraComponent';

const AddCookbookModal = ({ isOpen, onClose, onSave, editingCookbook }) => {
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (editingCookbook) {
        setTitle(editingCookbook.title);
      } else {
        setTitle('');
      }
      setError('');
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [editingCookbook, isOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Tab' && modalRef.current) {
        const focusableElements = Array.from(modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )).filter(el => el.offsetParent !== null);

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentIndex = focusableElements.indexOf(document.activeElement);

        event.preventDefault();

        if (event.shiftKey) {
          if (currentIndex <= 0) {
            lastElement.focus();
          } else {
            focusableElements[currentIndex - 1].focus();
          }
        } else {
          if (currentIndex === -1 || currentIndex === focusableElements.length - 1) {
            firstElement.focus();
          } else {
            focusableElements[currentIndex + 1].focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, editingCookbook, onClose]);

  if (!isOpen) return null;

  const handleSave = async (andAnother) => {
    if (!title) {
      setError('Cookbook title cannot be empty');
      return;
    }
    const response = await onSave({ ...editingCookbook, title }, andAnother);
    if (response && response.error) {
      setError(response.error);
    } else {
      setError('');
      if (andAnother) {
        setTitle('');
        inputRef.current.focus();
      } else {
        onClose();
      }
    }
  };

  const handleCapture = async (blob) => {
    setShowCamera(false);
    setIsProcessing(true);
    try {
      const res = await ocrImage(blob, 'cookbook-title');
      if (res && res.response) {
        setTitle(res.response);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setError(error.message || 'Error processing image.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="modal-overlay cookbook-modal">
      <div className="modal-content" ref={modalRef}>
        <div className="modal-header">
          <h2><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>menu_book</i>{editingCookbook ? 'Edit Cookbook' : 'Add Cookbook'}</h2>
          <button title="Close" onClick={onClose} className="icon-button close-button"><i className="material-icons">close</i></button>
        </div>
        {showCamera ? (
          <CameraComponent onCapture={handleCapture} onClose={() => setShowCamera(false)} />
        ) : (
          <>
            <div className="modal-body">
              {error && <p className="error-message">{error}</p>}
              <div className="search-input-container">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Cookbook Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave(!editingCookbook)}
                  disabled={isProcessing}
                />
                <button onClick={() => setShowCamera(true)} className="icon-button" title="Scan Cookbook Cover" disabled={isProcessing}><i className="material-icons">camera_alt</i></button>
                {isProcessing && (
                  <div className="clear-input-button" style={{ right: '50px' }}>
                    <i className="material-icons spin">autorenew</i>
                  </div>
                )}
                {title && !isProcessing && <button title="Clear title" onClick={() => { setTitle(''); inputRef.current.focus(); }} className="clear-input-button"><i className="material-icons">close</i></button>}
              </div>
            </div>
            <div className="modal-footer">
              {!editingCookbook && <button title="Save and add another" onClick={() => handleSave(true)}>Save and another</button>}
              <button title="Save and close" onClick={() => handleSave(false)}>Save and close</button>
              <button title="Cancel" onClick={onClose} className="cancel-button">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddCookbookModal;